import { joiResolver } from '@hookform/resolvers/joi';
import { useForm, UseFormReturn } from 'react-hook-form';
import * as Joi from 'joi';
import dayjs from 'dayjs';
import { useState } from 'react';
import { MutationStatus } from 'react-query';
import { useGetRegistrationDocument } from '../../../api-refactored/candidate/useGetRegistrantionReportDocument';
import { FileUploadState } from '../../../models/File';
import { usePostGenerateReport } from '../../../api-refactored/candidate/usePostGenerateReport';

interface EditDeleteReturn {
  /**
   * Perform submit action, either to call block call or unblock call, depends on the account status
   */
  submitGenerate: (submitData: any) => void;
  /**
   * Form to send edit & delete
   */
  form: UseFormReturn<Record<string, any>>;
  /**
   * React query mutation object
   */
  generateReportMutation: any;

  getDocumentUploadStatus: any;

  fileDownloadStatus: FileUploadState;
  onDialogClose: () => void;
  dialogState: boolean;
  process: MutationStatus;
}

/**
 * A hook to handle search functionality
 * @returns
 */
export const useRegistrationReport = (): EditDeleteReturn => {
  const [fileUUID, setFileUUID] = useState('');
  const [process, setProcess] = useState<MutationStatus>('idle');
  const [fileDownloadStatus, setFileDownloadStatus] = useState<FileUploadState>(
    FileUploadState.IDLE
  );
  const [dialogState, setDialogState] = useState<boolean>(false);

  const form = useForm({
    mode: 'onSubmit',
    resolver: async (data, context, options) => joiResolver(schema)(data, context, options),
  });

  const getDocumentUploadStatus = useGetRegistrationDocument({
    fileKey: fileUUID || '',
    enabled: !!fileUUID && process === 'loading',
    refetchInterval: 5000,
    onSuccess: (res) => {
      setDialogState(true);
      setFileDownloadStatus(res.fileStatus);

      if (res.fileStatus === FileUploadState.FAILED || res.fileStatus === FileUploadState.TIMEOUT) {
        setProcess('error');
        setFileUUID('');
      }

      if (res.fileStatus === FileUploadState.UPLOADING) {
        setProcess('loading');
      }

      if (res.fileStatus === FileUploadState.SUCCESS) {
        setProcess('success');

        const newWindow = window.open(res.url, '_blank', 'noopener,noreferrer');
        if (newWindow) {
          newWindow.opener = null;
        }
        setFileUUID('');
        setDialogState(false);
      }
    },
    onError: () => {
      setDialogState(false);
    },
  });

  const generateReportMutation = usePostGenerateReport({
    onSuccess: (res) => {
      setFileUUID(res.uuid);
    },
    onError: (err) => null,
  });

  const submitGenerate = (submitData: any) => {
    setProcess('loading');
    generateReportMutation.mutate({
      subjectExamLevel: submitData?.subjectExamLevel || [],
      candidateExamLevel: submitData?.candidateExamLevel || [],
      examSeries: submitData?.examSeries || [],
      startDate: dayjs(submitData.startDate).format('YYYY-MM-DDT00:00:00'),
      endDate: dayjs(submitData.endDate).format('YYYY-MM-DDT23:59:59'),
    });
  };

  const onDialogClose = () => {
    setDialogState(false);
  };

  return {
    form,
    submitGenerate,
    generateReportMutation,
    getDocumentUploadStatus,
    fileDownloadStatus,
    onDialogClose,
    dialogState,
    process,
  };
};

const schema = Joi.object({
  startDate: Joi.string().required().messages({
    'date.base': 'Application Start Date not allowed to be empty.',
    'date.max': 'Application Start Date should not be greater than current date.',
    'any.required': 'Application Start Date not allowed to be empty.',
    'string.empty': 'Application Start Date not allowed to be empty.',
  }),
  endDate: Joi.string().required().messages({
    'date.base': 'Application End Date not allowed to be empty.',
    'date.max': 'Application End Date should not be greater than current date.',
    'any.required': 'Application End Date not allowed to be empty.',
    'string.empty': 'Application End Date not allowed to be empty.',
  }),
  selectedExamLevel: Joi.string().required().messages({
    'any.required': 'Please select one exam level type',
    'string.empty': 'Please select one exam level type',
    'string.base': 'Please select one exam level type',
  }),
  subjectExamLevel: Joi.array()
    .items(Joi.object({ code: Joi.string(), description: Joi.string(), flag: Joi.boolean() }))
    .has(Joi.object({ code: Joi.string(), description: Joi.string(), flag: Joi.boolean() }))
    .messages({
      'array.hasUnknown': 'Please select exam level',
    }),
  candidateExamLevel: Joi.array()
    .items(Joi.object({ code: Joi.string(), description: Joi.string() }))
    .has(Joi.object({ code: Joi.string(), description: Joi.string() }))
    .messages({
      'array.hasUnknown': 'Please select exam level',
    }),
  examSeries: Joi.any(),
});
